import { Box, Flex, Table, Tag, Tbody, Td, Text, Th, Thead, Tr, useColorModeValue } from '@chakra-ui/react';
import {
    createColumnHelper,
    flexRender,
    getCoreRowModel,
    getSortedRowModel,
    SortingState,
    useReactTable
} from '@tanstack/react-table';
// Custom components
import { useMemo, useState } from 'react';
import PageCard from '../../../components/page-card';
import ErrorState from '../../../components/states/ErrorState';
import SortIndicator from '../../../components/table/SortIndicator';
import { translate } from '../../../i18n';
import ScopePrompt from '../../_shared/ScopePrompt';
import { classifyRead } from '../../_shared/readState';
import { MembershipVariantPaymentMode, UserMembershipDiscountType, UserMembershipTypeDto } from '../../../models/user-membership/dto/user-membership.dto';
import { tableProps, tableThProps, tableRowProps, tableCellProps } from '../../../theme/constants';
import { useSelectedOrganization } from '../../organization/hooks/useSelectedOrganization';
import { useGetTimeWindowGroups } from '../../time-windows/hooks/useGetTimeWindowGroups';
import { TimeWindowGroupList } from '../../time-windows/TimeWindowGroupList';
import { useGetUserMembershipTypes } from '../hooks/useGetUserMembershipTypes';
import AddUserMembershipTypeModal from './AddUserMembershipTypeModal';

const columnHelper = createColumnHelper<UserMembershipTypeDto>();

const MS_PER_DAY = 24 * 60 * 60 * 1000;

const msToDays = (ms: number) => ms / MS_PER_DAY;

const formatDiscountValue = (value: number, type: UserMembershipDiscountType) => {
    return type === UserMembershipDiscountType.PERCENTAGE ? `${value}%` : value;
};

const formatPrice = (priceInCents?: number, currency?: string) => {
    // MYS-125 P2.2b: `!priceInCents` treats a genuine 0 € price (e.g. the
    // DUO NON PAYANT variant, or any free tier) the same as "no price set",
    // rendering '-' instead of '€0.00' — the exact money-units misrender
    // flagged in P2.1's Decisions log. Only undefined/null means "no price".
    if (priceInCents === undefined || priceInCents === null) return '-';
    const price = (priceInCents / 100).toFixed(2);
    const currencySymbol = currency === 'usd' ? '$' : currency === 'gbp' ? '£' : '€';
    return `${currencySymbol}${price}`;
};

export default function UserMembershipTypesList() {
    const { selectedOrganization } = useSelectedOrganization();
    const [sorting, setSorting] = useState<SortingState>([]);
    // Holds only the id, not a snapshot of the row — variant CRUD (see
    // useCreateMembershipVariant/useUpdateMembershipVariant/
    // useDeleteMembershipVariant) patches the membershipTypes query cache on
    // success, but a `{ ...row.original }` copy taken at click-time would
    // never see that update: the modal (and MembershipVariantsSection inside
    // it) would keep rendering the stale variants list until a full page
    // refresh re-fetched everything. Deriving the edited type live from
    // `membershipTypes` below means every cache patch is picked up
    // immediately while the modal is open.
    const [editingMembershipTypeId, setEditingMembershipTypeId] = useState<string>();
    /**
     * Classified on the RAW `data`, not on the `= []` default, and that is the
     * R105-1 trap stated as code: `useGetUserMembershipTypes` is gated
     * `enabled: Boolean(organizationId)` and this page has no scope guard of
     * any kind, so with no organization selected React Query v4 parks the query
     * at `isLoading: true` / `fetchStatus: 'idle'` and the raw flag below used
     * to render "Loading…" FOREVER. Swapping that flag for `isInitialLoading`
     * alone would have dropped the page into the table with zero rows beside a
     * live "Add membership type" button — the same false claim the error branch
     * below was written (run 92) to stop, reached from the other side.
     *
     * Note what that branch shows about this file: the refused read was fixed
     * here, carefully, and the never-issued read on the SAME query was never
     * asked about. That is run 108's rider — a file already fixed once for this
     * class reads as safer than an untouched one.
     */
    const {
        data: membershipTypesData,
        isInitialLoading: isMembershipTypesInitialLoading,
        isError: isMembershipTypesError,
        refetch: refetchMembershipTypes,
    } = useGetUserMembershipTypes(selectedOrganization?.id);
    const membershipTypesReadState = classifyRead(
        isMembershipTypesError,
        isMembershipTypesInitialLoading,
        membershipTypesData,
    );
    // Memoised, unlike the `data = []` destructuring default it replaces: that
    // form hid the same new-identity-every-render from `react-hooks/
    // exhaustive-deps`, and this one feeds a `useMemo` and `useReactTable`.
    const membershipTypes = useMemo(() => membershipTypesData ?? [], [membershipTypesData]);
    const membershipTypeBeingEdited = useMemo(
        () => membershipTypes.find((type) => type.id === editingMembershipTypeId),
        [membershipTypes, editingMembershipTypeId],
    );
    const setMembershipTypeBeingEdited = (type?: UserMembershipTypeDto) => setEditingMembershipTypeId(type?.id);
    /**
     * R92-4 — same read, second symptom. This page uses the groups only to NAME
     * the per-time-window discounts inside three membership-type columns
     * (`timeWindowGroups.find(...)?.name`), so post-R89-1 a refused read
     * rendered each of them as `": 20%"` — a discount with no time window
     * attached to it, on the page the operator prices memberships from.
     *
     * The table is NOT withheld here: membership types come from a different
     * query whose own error branch is above (run 92), and dropping the page
     * because an unrelated read failed is exactly the defect this family of
     * fixes exists to close.
     */
    const { data: timeWindowGroups = [], isError: isTimeWindowGroupsError } =
        useGetTimeWindowGroups(selectedOrganization?.id);

    const columns = useMemo(() => [
        columnHelper.accessor('name', {
            header: translate('userMembership.name'),
            cell: info => (
                <Flex align="center" gap={2}>
                    <Text>{info.getValue()}</Text>
                    {info.row.original.isOrganizationDefault && (
                        <Tag colorScheme="brand" size="sm">
                            {translate('common.default')}
                        </Tag>
                    )}
                </Flex>
            ),
        }),
        columnHelper.accessor('sports', {
            header: translate('userMembership.sports_label'),
            cell: info => {
                const sports = info.getValue();
                const overrides = info.row.original.sportDiscounts ?? [];
                if (!sports?.length) {
                    return (
                        <Flex gap={1} wrap="wrap" align="center">
                            <Text fontSize="sm" color="gray.500">{translate('userMembership.sports_all')}</Text>
                            {overrides.map(override => (
                                <Tag key={override.sport} size="sm">
                                    {override.sport} · {formatDiscountValue(override.courtBookingDiscountValue, override.courtBookingDiscountType)}
                                </Tag>
                            ))}
                        </Flex>
                    );
                }
                return (
                    <Flex gap={1} wrap="wrap">
                        {sports.map(sport => {
                            const override = overrides.find(d => d.sport === sport);
                            return (
                                <Tag key={sport} size="sm">
                                    {sport}
                                    {override ? ` · ${formatDiscountValue(override.courtBookingDiscountValue, override.courtBookingDiscountType)}` : ''}
                                </Tag>
                            );
                        })}
                    </Flex>
                );
            },
        }),
        columnHelper.accessor('timeWindowDiscounts', {
            header: translate('userMembership.courtBookingDiscountValue'),
            cell: info => {
                const discounts = info.getValue();
                if (!discounts.length) return '-';
                return (
                    <Box>
                        <Text fontSize="sm">
                            {translate('common.default')}: {formatDiscountValue(info.row.original.courtBookingDiscountValue, info.row.original.courtBookingDiscountType)}
                        </Text>
                        {discounts.map(discount => {
                            const group = timeWindowGroups.find(g => g.id === discount.timeWindowGroupId);
                            if (discount.courtBookingDiscountValue === info.row.original.courtBookingDiscountValue) return null;
                            return (
                                <Text key={discount.id} fontSize="sm" color="gray.500">
                                    {group?.name}: {formatDiscountValue(discount.courtBookingDiscountValue, discount.courtBookingDiscountType)}
                                </Text>
                            );
                        })}
                    </Box>
                );
            },
        }),
        columnHelper.accessor('coachingSessionDiscountValue', {
            header: translate('userMembership.coachingSessionDiscountValue'),
            cell: info => formatDiscountValue(info.getValue(), info.row.original.coachingSessionDiscountType),
        }),
        columnHelper.accessor('eventJoiningDiscountValue', {
            header: translate('userMembership.eventJoiningDiscountValue'),
            // Rendered exactly like the court/coaching columns (bare number for a
            // CONSTANT, no currency symbol) so the three read consistently; the
            // euros-not-cents unit is spelled out in the form's helper text,
            // which is where an admin actually types a value.
            cell: info => formatDiscountValue(info.getValue(), info.row.original.eventJoiningDiscountType),
        }),
        columnHelper.accessor('maxCourtBookingTimeInAdvance', {
            header: translate('userMembership.maxCourtBookingTimeInAdvance'),
            cell: info => {
                const defaultValue = msToDays(info.getValue());
                const timeWindowValues = info.row.original.timeWindowDiscounts.map(discount => ({
                    groupName: timeWindowGroups.find(g => g.id === discount.timeWindowGroupId)?.name,
                    value: msToDays(discount.maxCourtBookingTimeInAdvance)
                })).filter(x => x.value !== defaultValue);

                return (
                    <Box>
                        <Text fontSize="sm">{translate('common.default')}: {defaultValue} {translate('userMembership.days')}</Text>
                        {timeWindowValues.map((tw, index) => (
                            <Text key={index} fontSize="sm" color="gray.500">
                                {tw.groupName}: {tw.value} {translate('userMembership.days')}
                            </Text>
                        ))}
                    </Box>
                );
            },
        }),
        columnHelper.accessor('maxPrivateCourtBookings', {
            header: translate('userMembership.maxPrivateCourtBookings'),
            cell: info => {
                const defaultValue = info.getValue();
                const timeWindowValues = info.row.original.timeWindowDiscounts.map(discount => ({
                    groupName: timeWindowGroups.find(g => g.id === discount.timeWindowGroupId)?.name,
                    value: discount.maxPrivateCourtBookings,
                })).filter(x => x.value !== defaultValue);

                return (
                    <Box>
                        <Text fontSize="sm">
                            {translate('common.default')}: {defaultValue}
                        </Text>
                        {timeWindowValues.map((tw, index) => (
                            <Text key={index} fontSize="sm" color="gray.500">
                                {tw.groupName}: {tw.value}
                            </Text>
                        ))}
                    </Box>
                );
            },
        }),
        columnHelper.accessor('maxPrivateCourtBookingsDuration', {
            header: translate('userMembership.maxPrivateCourtBookingsDuration'),
            cell: info => {
                const defaultValue = msToDays(info.getValue());
                const timeWindowValues = info.row.original.timeWindowDiscounts.map(discount => ({
                    groupName: timeWindowGroups.find(g => g.id === discount.timeWindowGroupId)?.name,
                    value: msToDays(discount.maxPrivateCourtBookingsDuration)
                })).filter(x => x.value !== defaultValue);

                return (
                    <Box>
                        <Text fontSize="sm">
                            {translate('common.default')}: {defaultValue} {translate('userMembership.days')}
                        </Text>
                        {timeWindowValues.map((tw, index) => (
                            <Text key={index} fontSize="sm" color="gray.500">
                                {tw.groupName}: {tw.value} {translate('userMembership.days')}
                            </Text>
                        ))}
                    </Box>
                );
            },
        }),
        columnHelper.accessor('isSubscriptionBased', {
            header: translate('userMembership.subscription_settings'),
            cell: info => {
                const membershipType = info.row.original;
                // MYS-125 P2.2b: once a type has any live variant, price/billing
                // is authoritative on the variants themselves (D3 — the type's
                // own priceInCents/billingInterval just mirror the isDefault
                // variant, per P2.1's "legacy price locked" section below this
                // table). Show every live variant's own price/mode/commitment
                // rather than only the type-level price, mirroring what
                // AddUserMembershipTypeModal's variant editor already lets an
                // admin manage. `variants` is populated server-side ordered
                // isDefault DESC, priceInCents ASC (display convenience only —
                // NOT the D3 resolution rule, see get-membership-types.ts).
                const liveVariants = membershipType.variants ?? [];
                if (liveVariants.length > 0) {
                    return (
                        <Flex direction="column" gap={1}>
                            {liveVariants.map(variant => (
                                <Flex key={variant.id} align="center" gap={2} wrap="wrap">
                                    <Text fontSize="sm" fontWeight={variant.isDefault ? 'bold' : 'medium'}>
                                        {variant.label}
                                    </Text>
                                    <Text fontSize="sm" color="gray.500">
                                        {formatPrice(variant.priceInCents, variant.currency)}
                                    </Text>
                                    <Text fontSize="xs" color="gray.500">
                                        {variant.paymentMode === MembershipVariantPaymentMode.SINGLE
                                            ? translate('userMembership.variant_payment_mode_single')
                                            : translate('userMembership.variant_commitment_months_option', { count: variant.commitmentMonths })
                                        }
                                    </Text>
                                    {variant.isDefault && (
                                        <Tag colorScheme="brand" size="sm">
                                            {translate('common.default')}
                                        </Tag>
                                    )}
                                    {variant.isDuoBeneficiary && (
                                        <Tag colorScheme="blue" size="sm">
                                            {translate('userMembership.variant_is_duo_beneficiary')}
                                        </Tag>
                                    )}
                                </Flex>
                            ))}
                            {(membershipType.maxAttachedMembers ?? 0) >= 1 && (
                                <Tag colorScheme="brand" size="sm" mt={1} w="fit-content">
                                    {translate('userMembership.duo_plan_duo')}
                                </Tag>
                            )}
                        </Flex>
                    );
                }
                if (!membershipType.isSubscriptionBased) {
                    return <Text fontSize="sm" color="gray.500">-</Text>;
                }
                return (
                    <Box>
                        <Text fontSize="sm" fontWeight="bold">
                            {formatPrice(membershipType.priceInCents, membershipType.currency)}
                        </Text>
                        <Text fontSize="xs" color="gray.500">
                            {membershipType.billingInterval === 'monthly'
                                ? translate('userMembership.billing_monthly')
                                : translate('userMembership.billing_yearly')
                            }
                        </Text>
                        {/* MYS-117: Duo plan tag (label from i18n, model is a count). */}
                        {(membershipType.maxAttachedMembers ?? 0) >= 1 && (
                            <Tag colorScheme="brand" size="sm" mt={1}>
                                {translate('userMembership.duo_plan_duo')}
                            </Tag>
                        )}
                    </Box>
                );
            },
        }),
    ], [timeWindowGroups]);

    const table = useReactTable({
        columns,
        data: membershipTypes,
        state: {
            sorting,
        },
        onSortingChange: setSorting,
        getCoreRowModel: getCoreRowModel(),
        getSortedRowModel: getSortedRowModel(),
    });

    const borderColor = useColorModeValue('gray.200', 'gray.600');

    if (membershipTypesReadState === 'loading') {
        return <Box>{translate('time_windows.loading')}</Box>;
    }

    /**
     * Nobody asked. The read is gated on the organization picker in the navbar,
     * so neither the table nor the "Add membership type" button beside it can
     * say anything true yet.
     *
     * `TimeWindowGroupList` is deliberately NOT rendered here, unlike in the
     * error branch below. There the two reads had failed independently and the
     * groups section could still answer; here BOTH queries are gated on the
     * same missing selection, so the section would render its own identical
     * prompt (run 105 fixed it) and the operator would be told the same thing
     * twice.
     */
    if (membershipTypesReadState === 'never-issued') {
        return (
            <Box display='flex' flexDirection='column' gap={4}>
                <PageCard title={translate('userMembership.memberships')}>
                    <ScopePrompt gate='organization' />
                </PageCard>
            </Box>
        );
    }

    /**
     * A refused or failed read used to reach the table below with
     * `membershipTypes = []`, so the page rendered its headers over no rows —
     * "this organization has no membership types" — while still offering the
     * Add button beside the title. That pairing is how a duplicate gets
     * created: the operator is told the list is empty and invited to fill it.
     *
     * The Add action is deliberately withheld in this branch for the same
     * reason it was withheld from `CouponList` (run 91): creating a second
     * "Standard" membership type against a list we could not read is a write
     * nobody can undo from this page.
     */
    if (membershipTypesReadState === 'unreadable') {
        return (
            <Box display='flex' flexDirection='column' gap={4}>
                <PageCard title={translate('userMembership.memberships')}>
                    <ErrorState
                        message={translate('userMembership.error_loading')}
                        onRetry={() => { void refetchMembershipTypes(); }}
                    />
                </PageCard>
                {/*
                  * Kept in this branch deliberately. Time-window groups are a
                  * separate feature with its own query and its own error state;
                  * dropping the section because the membership read failed
                  * would be the same defect this branch exists to fix, one
                  * component over — an unrelated failure silently removing
                  * something the operator came here for.
                  */}
                <TimeWindowGroupList />
            </Box>
        );
    }

    return (
        <Box display='flex' flexDirection='column' gap={4}>
            <PageCard
                title={translate('userMembership.memberships')}
                rightHeader={
                    <AddUserMembershipTypeModal
                        selectedMembershipType={membershipTypeBeingEdited}
                        setSelectedMembershipType={setMembershipTypeBeingEdited}
                    />
                }
            >
                <Box overflowX={{ sm: 'scroll', xl: 'hidden' }}>
                    {isTimeWindowGroupsError && (
                        <Text color='danger.500' fontSize='sm' mb={2}>
                            {translate('time_windows.error_loading')}
                        </Text>
                    )}
                    <Table {...tableProps} mt={0}>
                        <Thead>
                            {table.getHeaderGroups().map((headerGroup) => (
                                <Tr key={headerGroup.id}>
                                    {headerGroup.headers.map((header) => {
                                        return (
                                            <Th
                                                key={header.id}
                                                colSpan={header.colSpan}
                                                {...tableThProps}
                                                borderColor={borderColor}
                                                cursor='pointer'
                                                onClick={header.column.getToggleSortingHandler()}
                                            >
                                                {header.isPlaceholder ? null : (
                                                    <Flex
                                                        {...{
                                                            className: header.column.getCanSort()
                                                                ? 'cursor-pointer select-none'
                                                                : '',
                                                        }}
                                                    >
                                                        {flexRender(
                                                            header.column.columnDef.header,
                                                            header.getContext()
                                                        )}
                                                        <SortIndicator direction={header.column.getIsSorted()} />
                                                    </Flex>
                                                )}
                                            </Th>
                                        );
                                    })}
                                </Tr>
                            ))}
                        </Thead>
                        <Tbody>
                            {table.getRowModel().rows.map((row) => {
                                return (
                                    <Tr key={row.id}
                                        {...tableRowProps}
                                        onClick={() => {
                                            setMembershipTypeBeingEdited(row.original);
                                        }}>
                                        {row.getVisibleCells().map((cell) => {
                                            return (
                                                <Td
                                                    key={cell.id}
                                                    {...tableCellProps}>
                                                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                                </Td>
                                            );
                                        })}
                                    </Tr>
                                );
                            })}
                        </Tbody>
                    </Table>
                </Box>
            </PageCard>
            <TimeWindowGroupList />
        </Box>
    );
}